import React from "react";
import { LongTextLabel, Menu, SplitButton, Tooltip } from "@sscs/ui";
import { DownloadIcon } from "@sscs/ui/icons";
import { ApprovalStatus, DisplaySize } from "Components";
import { useDialog } from "Hooks";
import { DownloadDialog } from "Modules/download";
import { Version } from "Types";
import { isApproved } from "Utils";
import styles from "./package.module.scss";

type VersionSelectorProps = {
  versions: Version[];
  selectedVersionId: string | null;
  inCatalog: boolean;
  onSelect: (versionId: string) => void;
  onDownload: () => void;
};

const VersionSelector = ({
  versions,
  selectedVersionId,
  inCatalog,
  onSelect,
  onDownload,
}: VersionSelectorProps): React.JSX.Element => {
  const downloadDialog = useDialog();

  return (
    <>
      {selectedVersionId && inCatalog && (
        <DownloadDialog
          isOpen={downloadDialog.isOpen}
          onClose={downloadDialog.close}
          fileID={selectedVersionId}
          fileType="VERSION"
          onDownload={onDownload}
        />
      )}
      <div className={styles["version-selector-container"]}>
        <SplitButton
          id="version-selector"
          aria-label="select-version-for-download"
          label="Download"
          startIcon={<DownloadIcon />}
          disabled={!inCatalog}
          tooltipTitle={
            inCatalog
              ? undefined
              : "Only packages in the catalog can be downloaded"
          }
          onButtonClick={downloadDialog.open}
          className={styles["download-button"]}
          dropdownClassName={styles["version-selector-dropdown"]}
        >
          <Menu.List id="split-button-menu" className={styles["download-menu"]}>
            <Menu.GroupTitle>Select a version to download</Menu.GroupTitle>
            {versions.map((version) => (
              <Tooltip
                key={version.version_id}
                title={
                  <span className={styles["download-reason"]}>
                    Only approved versions can be downloaded
                  </span>
                }
                describeChild
                disabled={isApproved(version.approval_status)}
              >
                <Menu.Item
                  selected={version.version_id === selectedVersionId}
                  onClick={() => {
                    if (!isApproved(version.approval_status)) return;
                    onSelect(version.version_id);
                  }}
                  className={styles["download-item"]}
                  disabled={!isApproved(version.approval_status)}
                >
                  <div className={styles["version-info"]}>
                    <LongTextLabel label={version.software_info.version} />
                    <div className={styles["file-info"]}>
                      <LongTextLabel label={version.file_name} />
                      &nbsp; &#183;&nbsp;
                      <DisplaySize
                        sizeInBytes={version.file_size}
                        fractionDigits={0}
                      />
                    </div>
                  </div>
                  <span className={styles["approval-status"]}>
                    <ApprovalStatus
                      status={version.approval_status}
                      info={version.approval_information}
                    />
                  </span>
                </Menu.Item>
              </Tooltip>
            ))}
          </Menu.List>
        </SplitButton>
      </div>
    </>
  );
};

export default VersionSelector;
